import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, ArrowLeft, Package, Clock, Truck, CheckCircle, CreditCard, MapPin, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending_payment: { label: "Pending Payment", color: "bg-yellow-100 text-yellow-800 border-yellow-400", icon: CreditCard, step: 0 },
  received:        { label: "Received",        color: "bg-blue-100 text-blue-800 border-blue-400",     icon: Package,    step: 1 },
  in_process:      { label: "In Process",      color: "bg-orange-100 text-orange-800 border-orange-400", icon: Clock,    step: 2 },
  completed:       { label: "Completed",       color: "bg-green-100 text-green-800 border-green-400",  icon: CheckCircle, step: 3 },
  shipped:         { label: "Shipped",          color: "bg-purple-100 text-purple-800 border-purple-400", icon: Truck,   step: 4 },
  rejected:        { label: "Rejected",         color: "bg-red-100 text-red-800 border-red-400",       icon: XCircle,    step: -1 },
};

const STEPS = ["pending_payment", "received", "in_process", "completed", "shipped"];

function StatusTimeline({ currentStatus }) {
  const currentStep = STATUS_CONFIG[currentStatus]?.step ?? -1;

  return (
    <div className="flex items-center w-full gap-1" data-testid="status-timeline">
      {STEPS.map((s, i) => {
        const cfg = STATUS_CONFIG[s];
        const Icon = cfg.icon;
        const isActive = i <= currentStep;
        const isCurrent = s === currentStatus;
        return (
          <div key={s} className="flex items-center flex-1">
            <div className={`flex flex-col items-center flex-shrink-0 ${isCurrent ? "scale-110" : ""}`}>
              <div className={`w-8 h-8 border-2 border-black flex items-center justify-center transition-all ${isActive ? cfg.color : "bg-zinc-100 text-zinc-400"} ${isCurrent ? "shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" : ""}`}
                data-testid={`timeline-step-${s}`}>
                <Icon size={14} strokeWidth={2.5} />
              </div>
              <span className={`text-[8px] font-heading font-bold uppercase mt-1 text-center leading-tight ${isActive ? "text-black" : "text-zinc-400"}`}>
                {cfg.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 ${i < currentStep ? "bg-black" : "bg-zinc-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function OrderCard({ order }) {
  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.received;
  const shortId = order.id?.slice(0, 8) || "";
  const dateStr = order.created_at ? new Date(order.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

  return (
    <div className="brutal-card overflow-hidden" data-testid={`track-order-${order.id}`}>
      {/* Header */}
      <div className="p-5 border-b-2 border-black flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="font-mono text-[10px] tracking-wider text-zinc-400 uppercase block">Order #{shortId}</span>
          <h3 className="font-heading font-bold text-sm uppercase">{order.product_name}</h3>
          {order.quantity > 1 && <span className="text-xs text-zinc-500">Qty: {order.quantity}</span>}
        </div>
        <div className="text-right">
          <span className={`inline-block px-3 py-1 border-2 border-black text-xs font-heading font-bold uppercase ${cfg.color}`} data-testid={`order-status-badge-${order.id}`}>
            {cfg.label}
          </span>
          <span className="block text-[10px] text-zinc-400 mt-1">{dateStr}</span>
        </div>
      </div>

      {/* Timeline */}
      <div className="p-5 bg-zinc-50">
        <StatusTimeline currentStatus={order.status} />
      </div>

      {/* Details */}
      <div className="p-5 space-y-3 text-sm">
        {order.front_engravings?.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-zinc-400 font-heading text-[10px] uppercase w-24">Engravings</span>
            <span>{order.front_engravings.map(e => e.name).join(", ")}</span>
          </div>
        )}
        {order.cutout_shape_name && (
          <div className="flex items-center gap-2">
            <span className="text-zinc-400 font-heading text-[10px] uppercase w-24">Cutout</span>
            <span>{order.cutout_shape_name}</span>
          </div>
        )}
        {order.estimated_arrival_date && (
          <div className="flex items-center gap-2">
            <span className="text-zinc-400 font-heading text-[10px] uppercase w-24">Est. Arrival</span>
            <span className="font-bold text-[#FF3366]">{new Date(order.estimated_arrival_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
          </div>
        )}
        {order.pickup_on_site && (
          <div className="flex items-center gap-2 bg-[#00E5FF]/10 border border-[#00E5FF] px-3 py-2">
            <MapPin size={14} strokeWidth={2.5} className="text-[#00E5FF]" />
            <span className="text-xs font-heading font-bold uppercase">Event Pickup</span>
          </div>
        )}
        {order.status === "rejected" && order.rejection_reason && (
          <div className="bg-red-50 border-2 border-red-300 p-3" data-testid={`rejection-reason-track-${order.id}`}>
            <span className="font-heading font-bold text-[10px] uppercase text-red-500 block mb-1">Rejection Reason</span>
            <p className="text-xs text-red-700">{order.rejection_reason}</p>
            <p className="text-xs text-zinc-500 mt-2">Your card was not charged. Feel free to place a new order with a different design.</p>
          </div>
        )}
        <div className="flex items-center justify-between pt-2 border-t border-zinc-200">
          <span className="text-zinc-400 font-heading text-[10px] uppercase">Total</span>
          <span className="font-heading font-black text-lg text-[#FF3366]">${(order.total || 0).toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

export default function TrackOrderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [searchType, setSearchType] = useState("email");
  const [orders, setOrders] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const doSearch = async (q, type) => {
    if (!q.trim()) return;
    setLoading(true);
    setError("");
    setSearched(true);
    try {
      const params = type === "email" ? { email: q.trim() } : { order_id: q.trim() };
      const res = await axios.get(`${API}/orders/track`, { params });
      setOrders(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => doSearch(query, searchType);

  useEffect(() => {
    const urlType = searchParams.get("type");
    const urlQ = searchParams.get("q");
    if (urlQ) {
      const t = urlType === "email" ? "email" : "order_id";
      setSearchType(t);
      setQuery(urlQ);
      doSearch(urlQ, t);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hideHome = searchParams.get("hideHome") === "1";

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="track-order-page">
      {/* Nav */}
      <div className="border-b-2 border-black bg-white px-6 py-4 flex items-center justify-between">
        {!hideHome ? (
          <button onClick={() => navigate("/")} className="flex items-center gap-2 font-heading font-bold text-sm uppercase tracking-wider hover:text-[#FF3366] transition-colors" data-testid="back-to-home">
            <ArrowLeft size={18} strokeWidth={2.5} /> Back to Shop
          </button>
        ) : <div />}
        <span className="font-heading font-bold text-sm uppercase tracking-wider flex items-center gap-2">
          <Search size={16} strokeWidth={2.5} /> Track Order
        </span>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase mb-3">Track Your Order</h1>
          <p className="text-zinc-500 text-sm max-w-md mx-auto">Enter your email address or order ID to check the status of your laser engraving order.</p>
        </div>

        {/* Search Box */}
        <div className="brutal-card p-6 mb-8" data-testid="search-box">
          {/* Type Toggle */}
          <div className="flex gap-2 mb-4">
            <button onClick={() => { setSearchType("email"); setQuery(""); setOrders([]); setSearched(false); }}
              className={`px-4 py-2 border-2 border-black font-heading font-bold text-xs uppercase transition-all ${searchType === "email" ? "bg-[#FF3366] text-white shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]" : "bg-white"}`}
              data-testid="search-by-email-btn">
              By Email
            </button>
            <button onClick={() => { setSearchType("order_id"); setQuery(""); setOrders([]); setSearched(false); }}
              className={`px-4 py-2 border-2 border-black font-heading font-bold text-xs uppercase transition-all ${searchType === "order_id" ? "bg-[#FF3366] text-white shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]" : "bg-white"}`}
              data-testid="search-by-id-btn">
              By Order ID
            </button>
          </div>

          {/* Input */}
          <div className="flex gap-3">
            <input
              className="brutal-input flex-1"
              placeholder={searchType === "email" ? "your@email.com" : "e.g. a066e047"}
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
              data-testid="track-search-input"
            />
            <button onClick={handleSearch} disabled={!query.trim() || loading}
              className={`brutal-btn text-sm flex items-center gap-2 ${!query.trim() || loading ? "opacity-50" : ""}`}
              data-testid="track-search-btn">
              <Search size={14} strokeWidth={2.5} /> {loading ? "Searching..." : "Search"}
            </button>
          </div>
          {error && <p className="text-[#FF3366] text-xs font-bold mt-3" data-testid="track-error">{error}</p>}
        </div>

        {/* Results */}
        {searched && !loading && (
          orders.length === 0 ? (
            <div className="brutal-card p-12 text-center" data-testid="no-orders-found">
              <Package size={48} className="mx-auto mb-4 text-zinc-300" strokeWidth={2} />
              <h3 className="font-heading text-lg font-bold uppercase mb-2">No Orders Found</h3>
              <p className="text-zinc-500 text-sm">
                {searchType === "email"
                  ? "We couldn't find any orders with that email. Please check your spelling and try again."
                  : "No order matches that ID. Try searching by email instead."}
              </p>
            </div>
          ) : (
            <div className="space-y-6" data-testid="order-results">
              <p className="font-heading font-bold text-xs uppercase text-zinc-500">
                {orders.length} order{orders.length !== 1 ? "s" : ""} found
              </p>
              {orders.map(order => (
                <OrderCard key={order.id} order={order} />
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
